import type { SortDescriptor } from "react-aria-components";

import { type ReactNode, useMemo, useRef } from "react";

import { ErrorBoundary } from "@sentry/react";
import clsx from "clsx";
import { Table as AriaTable, TableHeader as AriaTableHeader } from "react-aria-components";

import { TableBody } from "./table-body";
import { TableEmptyState } from "./table-empty-state";
import { TableFooter } from "./table-footer";
import { TableHeader } from "./table-header";
import { useScrollbarVisible } from "./use-scrollbar-visible";

import styles from "./table.module.css";

type TableProps<T extends { id: string }> = {
	className?: string;
	/**
	 * @description The current page number of the table.
	 */
	currentPage?: number;
	/**
	 * @description The data to be displayed in the table. Each item must have a unique `id` property.
	 */
	data: T[];
	/**
	 * @description An element to be displayed when the `data` array is empty and `isLoading` is `false`.
	 */
	emptyState?: ReactNode;
	/**
	 * @description Whether there are active filters applied to the table.
	 */
	hasActiveFilters?: boolean;
	/**
	 * @description The content to be rendered in the table header. Needs to be a valid "react-aria-component"
	 * `<TableHeader />` children (i.e. `<Column isRowHeader />`).
	 */
	header: ReactNode;
	/**
	 * @description Whether the table is currently loading data. When `true`, the table will render loading rows instead of the actual data.
	 */
	isLoading: boolean;
	/**
	 * @description Whether the "next page" button in the table footer should be disabled.
	 */
	isNextDisabled?: boolean;
	/**
	 * @description Whether the "previous page" button in the table footer should be disabled.
	 */
	isPrevDisabled?: boolean;
	/**
	 * @description A callback function that is called when the user navigates to the next or previous page of the table.
	 * If undefined, the pagination buttons will not be rendered in the table footer.
	 */
	onPageNavigation?: (direction: "next" | "prev") => void;
	/**
	 * @description A callback function that is called when the user selects a new number of items to display per page in the table footer.
	 * If undefined, the per page selector will not be rendered.
	 */
	onPerPageChange?: (perPage: number) => void;
	/**
	 * @description A callback function that is called when the user sorts a column that has `allowsSorting`.
	 * The table is fully controlled: it is up to the consumer to sort the `data` (or refetch it) in response.
	 * If undefined, sorting interactions are disabled.
	 */
	onSortChange?: (sortDescriptor: SortDescriptor) => void;
	/**
	 * @description The current number of items displayed per page.
	 */
	perPage?: number;
	/**
	 * @description A callback function that is called when the user clicks the refresh button in the table footer.
	 * If undefined, the refresh button will not be rendered.
	 */
	refreshData?: () => void;
	/**
	 * @description A function that returns a row to be displayed for each item in the `data` array.
	 * It receives the item as a parameter and should return a valid "react-aria-component" `<Row />` with the appropriate number
	 * of `<Cell />` children to match the table header.
	 * The `id` needs to be passded in the wrapping component, e.g. `<Row id={item.id}>...</Row>`.
	 */
	row: (item: T) => ReactNode;
	/**
	 * @description The column currently sorted and its direction, e.g. `{ column: "name", direction: "ascending" }`.
	 * The `column` must match the `id` of a `<TableColumn />` in the `header`.
	 */
	sortDescriptor?: SortDescriptor;
	/**
	 * @description The title of the table, used for accessibility purposes. It should describe the content of the table.
	 */
	title: string;
	/**
	 * @description The total number of pages available in the table.
	 */
	totalPages?: number;
};

export function Table<T extends { id: string }>({
	className,
	currentPage,
	data,
	emptyState,
	hasActiveFilters,
	header,
	isLoading,
	isNextDisabled,
	isPrevDisabled,
	onPageNavigation,
	onPerPageChange,
	onSortChange,
	perPage,
	refreshData,
	row,
	sortDescriptor,
	title,
	totalPages,
}: TableProps<T>): ReactNode {
	const bodyRef = useRef<HTMLTableSectionElement>(null);
	const containerRef = useRef<HTMLTableElement>(null);
	const headerRef = useRef<HTMLTableSectionElement>(null);
	const isScrollbarVisible = useScrollbarVisible(bodyRef, containerRef, [data, isLoading]);

	const showEmptyState = useMemo(() => {
		return !isLoading && data.length === 0;
	}, [isLoading, data.length]);

	return (
		<ErrorBoundary>
			<div className={clsx(styles.container, className)}>
				<AriaTable
					aria-label={title}
					className={styles.table}
					onSortChange={onSortChange}
					ref={containerRef}
					sortDescriptor={sortDescriptor}
				>
					<AriaTableHeader className={styles.header} ref={headerRef}>
						<TableHeader>{header}</TableHeader>
					</AriaTableHeader>
					<TableBody
						bodyRef={bodyRef}
						data={data}
						isLoading={isLoading}
						isScrollbarVisible={isScrollbarVisible}
						row={row}
					/>
				</AriaTable>
				<TableEmptyState
					emptyState={emptyState}
					hasActiveFilters={hasActiveFilters || false}
					showEmptyState={showEmptyState}
				/>
				<TableFooter
					currentPage={currentPage}
					isEmptyState={showEmptyState}
					isNextDisabled={isNextDisabled}
					isPrevDisabled={isPrevDisabled}
					onPageNavigation={onPageNavigation}
					perPage={perPage}
					refreshData={refreshData}
					setPerPage={onPerPageChange}
					totalPages={totalPages}
				/>
			</div>
		</ErrorBoundary>
	);
}
